import type { CoreStats, Skill } from './types/game';
import type { SkillDefinition } from './skills';
import type { EssenceType } from './types/essence';

/**
 * Every skill in Sorrem, by branch and group (docs/gameplay/skill-tree-ui.md,
 * Skill Roster). The skill tree draws from this, and every character carries
 * every skill in it, starting at level 1 (see `withCatalogSkills`).
 *
 * Many skills here have no gameplay yet: they appear on the tree and can be
 * held, but nothing in a Crucible trains them (`trained: false`).
 */

/** The skill tree's branches, in tree order. */
export const SKILL_BRANCHES = ['martial', 'essence', 'crafting'] as const;

export type SkillBranch = (typeof SKILL_BRANCHES)[number];

/** Each branch's player-facing name: the mastery its dial shows. */
export const SKILL_BRANCH_NAMES: Readonly<Record<SkillBranch, string>> = {
    martial: 'Martial Discipline',
    essence: 'Veil Attunement',
    crafting: 'Crafting',
};

/** A grouping root on the skill tree: a named junction its skills hang from, with no progress of its own. */
export interface SkillGroupDefinition {
    readonly id: string;
    readonly branch: SkillBranch;
    readonly name: string;
    /** One player-facing line on what holds its skills together, shown when the group is selected. */
    readonly blurb: string;
    /** The essence type an Essence group organizes; it tints the group and its skills. Unset on other branches. */
    readonly essenceType?: EssenceType;
}

/**
 * How much a skill feeds a core stat, as docs/gameplay/skill-definitions.md
 * writes it: 3 is `+++` (primary), 2 is `++` (secondary), 1 is `+` (minor).
 */
export type SkillStatWeight = 1 | 2 | 3;

export type SkillStatWeights = Readonly<Partial<Record<keyof CoreStats, SkillStatWeight>>>;

export interface SkillCatalogEntry extends SkillDefinition {
    readonly branch: SkillBranch;
    /** The id of the group it hangs from (`SKILL_GROUPS`). */
    readonly group: string;
    /** Something in a Crucible trains it today; false while its rules are still to be designed. */
    readonly isTrained: boolean;
    /** The core stats it feeds ("Stats fed" in skill-definitions.md); crafting skills feed none. */
    readonly stats: SkillStatWeights;
    /** Praxis comes no faster than once this often, so spamming an action can't train it (martial skills). */
    readonly praxisCooldownMs?: number;
    /** Player-facing: what trains it in a Crucible ("Landing hits with a mace or hammer."). Unset where the tree says it generically. */
    readonly growsBy?: string;
}

/** In tree order: each branch's groups, from one side of its fan to the other. */
export const SKILL_GROUPS: readonly SkillGroupDefinition[] = [
    { id: 'martial-melee', branch: 'martial', name: 'Melee', blurb: 'Steel in hand, or none at all: the close work of blade, haft, and fist.' },
    { id: 'martial-ranged', branch: 'martial', name: 'Ranged', blurb: 'Death at a distance, for a steady hand and a patient eye.' },
    { id: 'martial-defense', branch: 'martial', name: 'Defense', blurb: 'The craft of not being struck: a shield raised in time, a blade turned aside.' },
    // Essence groups are the six essence types. They organize skills only: every cast still draws on the one
    // shared essence pool, never on a typed essence (see docs/gameplay/essence-abilities.md).
    { id: 'essence-fury', branch: 'essence', name: 'Fury', essenceType: 'fury', blurb: 'Rage made shape: power that burns hot, and does not care what else it burns.' },
    { id: 'essence-courage', branch: 'essence', name: 'Courage', essenceType: 'courage', blurb: 'The will to stand, and to make others stand with you.' },
    { id: 'essence-sorrow', branch: 'essence', name: 'Sorrow', essenceType: 'sorrow', blurb: 'Grief that mends, and grief that slowly unmakes.' },
    { id: 'essence-memory', branch: 'essence', name: 'Memory', essenceType: 'memory', blurb: 'What the Veil remembers: the weak point, the hidden thing, the echo of what came before.' },
    { id: 'essence-truth', branch: 'essence', name: 'Truth', essenceType: 'truth', blurb: 'Seeing what is, and refusing what is not.' },
    { id: 'essence-resonance', branch: 'essence', name: 'Resonance', essenceType: 'resonance', blurb: 'The plain stuff of essence, before any feeling shapes it.' },
    { id: 'crafting-smithing', branch: 'crafting', name: 'Smithing', blurb: 'Weapons and armor, hammered from steel.' },
    { id: 'crafting-jewelcrafting', branch: 'crafting', name: 'Jewelcrafting', blurb: 'Stones cut, filled with essence, and set: in rings and amulets, or inlaid in steel.' },
];

type MartialEntry = Pick<SkillCatalogEntry, 'id' | 'name' | 'group' | 'focus' | 'activation' | 'isTrained' | 'stats' | 'growsBy'> & { readonly praxisCooldownMs: number };
type EssenceEntry = Pick<SkillCatalogEntry, 'id' | 'name' | 'group' | 'focus' | 'isTrained' | 'stats'>;
type CraftingEntry = Pick<SkillCatalogEntry, 'id' | 'name' | 'group'>;

/** Martial skills are active, need the right gear, and fade with disuse. */
function martial(entry: MartialEntry): SkillCatalogEntry {
    return { ...entry, branch: 'martial', category: 'active', decayProfile: 'fast' };
}

/** Essence skills are passive, always on, and never fade. */
function essence(entry: EssenceEntry): SkillCatalogEntry {
    return { ...entry, branch: 'essence', category: 'passive', activation: 'always', decayProfile: 'none' };
}

/** Crafting skills never fade; none are trained until crafting exists. */
function crafting(entry: CraftingEntry): SkillCatalogEntry {
    return { ...entry, branch: 'crafting', category: 'passive', activation: 'always', decayProfile: 'none', isTrained: false, stats: {} };
}

/** In tree order within each group. */
export const SKILL_CATALOG: readonly SkillCatalogEntry[] = [
    martial({ id: 'blade-mastery', name: 'Blade Mastery', group: 'martial-melee', focus: 'melee', activation: 'requires-melee', isTrained: true, stats: { melee: 3, speed: 1 }, praxisCooldownMs: 300, growsBy: 'Landing hits with a sword or dagger.' }),
    martial({ id: 'bludgeoning', name: 'Bludgeoning', group: 'martial-melee', focus: 'melee', activation: 'requires-melee', isTrained: true, stats: { melee: 3 }, praxisCooldownMs: 300, growsBy: 'Landing hits with a mace or hammer.' }),
    martial({ id: 'dual-wielding', name: 'Dual Wielding', group: 'martial-melee', focus: 'melee', activation: 'requires-dual', isTrained: true, stats: { melee: 2, speed: 2 }, praxisCooldownMs: 600, growsBy: 'Landing hits with a weapon in both slots.' }),
    martial({ id: 'unarmed', name: 'Unarmed', group: 'martial-melee', focus: 'melee', activation: 'requires-unarmed', isTrained: true, stats: { melee: 2, dodge: 1, speed: 1 }, praxisCooldownMs: 300, growsBy: 'Landing hits with bare hands.' }),
    martial({ id: 'archery', name: 'Archery', group: 'martial-ranged', focus: 'range', activation: 'requires-range', isTrained: true, stats: { range: 3, dodge: 1 }, praxisCooldownMs: 300, growsBy: 'Landing ranged hits.' }),
    // Blocking already trains Shield Mastery (crucible-host skills service).
    martial({ id: 'shield-mastery', name: 'Shield Mastery', group: 'martial-defense', focus: 'defense', activation: 'requires-shield', isTrained: true, stats: { dodge: 1, resistance: 2 }, praxisCooldownMs: 400, growsBy: 'Blocking attacks with a shield.' }),
    martial({ id: 'parry', name: 'Parry', group: 'martial-defense', focus: 'defense', activation: 'requires-melee', isTrained: true, stats: { dodge: 2, melee: 1 }, praxisCooldownMs: 400, growsBy: 'Turning aside melee attacks with a melee weapon.' }),

    // Wrath succeeds the retired elemental-magic skill: destructive shaping, trained by essence hits.
    essence({ id: 'wrath', name: 'Wrath', group: 'essence-fury', focus: 'essence', isTrained: true, stats: { conviction: 3 } }),
    essence({ id: 'frenzy', name: 'Frenzy', group: 'essence-fury', focus: 'essence', isTrained: false, stats: { speed: 2, melee: 1 } }),
    essence({ id: 'immolation', name: 'Immolation', group: 'essence-fury', focus: 'essence', isTrained: false, stats: { conviction: 2, resistance: 1 } }),
    essence({ id: 'battle-instincts', name: 'Battle Instincts', group: 'essence-courage', focus: 'defense', isTrained: true, stats: { dodge: 2, resistance: 1 } }),
    essence({ id: 'valor', name: 'Valor', group: 'essence-courage', focus: 'defense', isTrained: false, stats: { resistance: 2, melee: 1 } }),
    essence({ id: 'smite', name: 'Smite', group: 'essence-courage', focus: 'essence', isTrained: false, stats: { conviction: 2, melee: 1 } }),
    essence({ id: 'restoration', name: 'Restoration', group: 'essence-sorrow', focus: 'essence', isTrained: false, stats: { resistance: 2, conviction: 1 } }),
    essence({ id: 'entropy', name: 'Entropy', group: 'essence-sorrow', focus: 'essence', isTrained: true, stats: { conviction: 2, resistance: 1 } }),
    // The sorrow of one who stays so others can go: its ability holds a doorway (The Held Door).
    essence({ id: 'parting', name: 'Parting', group: 'essence-sorrow', focus: 'defense', isTrained: true, stats: { resistance: 2, conviction: 1 } }),
    essence({ id: 'insight', name: 'Insight', group: 'essence-memory', focus: 'essence', isTrained: false, stats: { conviction: 2, dodge: 1 } }),
    essence({ id: 'echo', name: 'Echo', group: 'essence-memory', focus: 'essence', isTrained: false, stats: { conviction: 2, speed: 1 } }),
    essence({ id: 'warding', name: 'Warding', group: 'essence-truth', focus: 'defense', isTrained: false, stats: { resistance: 2, dodge: 1 } }),
    essence({ id: 'veilguard', name: 'Veilguard', group: 'essence-truth', focus: 'defense', isTrained: true, stats: { resistance: 3 } }),
    essence({ id: 'attune', name: 'Attune', group: 'essence-resonance', focus: 'essence', isTrained: false, stats: { conviction: 1, resistance: 1 } }),
    essence({ id: 'channel', name: 'Channel', group: 'essence-resonance', focus: 'essence', isTrained: false, stats: { conviction: 3 } }),
    essence({ id: 'harmony', name: 'Harmony', group: 'essence-resonance', focus: 'essence', isTrained: false, stats: { resistance: 2, conviction: 1 } }),

    crafting({ id: 'weaponsmithing', name: 'Weaponsmithing', group: 'crafting-smithing' }),
    crafting({ id: 'armorsmithing', name: 'Armorsmithing', group: 'crafting-smithing' }),
    crafting({ id: 'gemcutting', name: 'Gemcutting', group: 'crafting-jewelcrafting' }),
    // Filling natural stones with essence: an essence stone is a vessel, never an Empyreal crystal.
    crafting({ id: 'stone-infusion', name: 'Stone Infusion', group: 'crafting-jewelcrafting' }),
    crafting({ id: 'inlay', name: 'Inlay', group: 'crafting-jewelcrafting' }),
    crafting({ id: 'setting', name: 'Setting', group: 'crafting-jewelcrafting' }),
];

const CATALOG_BY_ID: ReadonlyMap<string, SkillCatalogEntry> = new Map(SKILL_CATALOG.map((entry) => [entry.id, entry]));

/** Retired skills and the skill that inherits their progress. */
export const RETIRED_SKILL_SUCCESSORS: Readonly<Record<string, string>> = {
    'elemental-magic': 'wrath',
    'spell-ward': 'veilguard',
};

export function findSkillCatalogEntry(id: string): SkillCatalogEntry | undefined {
    return CATALOG_BY_ID.get(id);
}

const GROUPS_BY_ID: ReadonlyMap<string, SkillGroupDefinition> = new Map(SKILL_GROUPS.map((group) => [group.id, group]));

/** The skill group with this id, or undefined for an unknown id. */
export function findSkillGroup(id: string): SkillGroupDefinition | undefined {
    return GROUPS_BY_ID.get(id);
}

/** A fresh, untrained skill from the catalog. */
export function createCatalogSkill(entry: SkillCatalogEntry): Skill {
    return {
        id: entry.id,
        name: entry.name,
        level: 1,
        praxis: 0,
        inactiveTicks: 0,
        focus: entry.focus,
        category: entry.category,
        activation: entry.activation,
        decayProfile: entry.decayProfile,
        isPinned: false,
    };
}

/**
 * A character's skills with every catalog skill present: missing ones start
 * at level 1, and a retired skill's progress moves to its successor (unless
 * the character already has the successor). Skills outside the catalog are
 * kept as they are. Catalog skills come first, in catalog order.
 */
export function withCatalogSkills(skills: readonly Skill[]): Skill[] {
    const byId = new Map<string, Skill>();
    for (const skill of skills) {
        const successor = RETIRED_SKILL_SUCCESSORS[skill.id];
        if (successor) continue;
        byId.set(skill.id, skill);
    }
    for (const skill of skills) {
        const successorId = RETIRED_SKILL_SUCCESSORS[skill.id];
        const successor = successorId ? findSkillCatalogEntry(successorId) : undefined;
        if (successor && !byId.has(successor.id)) {
            byId.set(successor.id, { ...createCatalogSkill(successor), level: skill.level, praxis: skill.praxis, isPinned: skill.isPinned ?? false });
        }
    }
    const fromCatalog = SKILL_CATALOG.map((entry) => byId.get(entry.id) ?? createCatalogSkill(entry));
    const others = [...byId.values()].filter((skill) => !CATALOG_BY_ID.has(skill.id));
    return [...fromCatalog, ...others];
}
